import React, { useEffect, useRef } from "react";
import { RouteComponentProps, StaticContext } from "react-router";
import { Box, Typography } from "@material-ui/core";
import {
  ActionLinks,
  BannerSeverity,
  IActionLink,
  Icon,
  IconName,
  MyTripsMobileCard,
  NotificationBanner,
} from "halifax";
import clsx from "clsx";
import { useInView } from "react-intersection-observer";
import {
  HotelItinerary,
  HotelItineraryState,
  ItineraryEnum,
  LodgingCollectionEnum,
  MyTripsFilter,
  VIEWED_LODGING_RESERVATION_IN_LIST,
  ViewedLodgingReservationInListProperties,
} from "redmond";
import { HotelCardConnectorProps } from "./container";
import * as textConstants from "./constants";
import { DesktopHotelCard } from "./components/DesktopHotelCard";
import { scrollTopWithOffset } from "../../component";
import { buildExploreActivitiesActions } from "../../helpers";
import { trackEvent } from "../../../../../../api/v1/analytics/trackEvent";
import { useIsTravelApp } from "@capone/common";

import "./styles.scss";
import { PersonalBusinessLabel } from "../../../capone-corporate/PersonalBusinessLabel";

export const getStatusBanner = (status?: HotelItineraryState) => {
  switch (status) {
    case HotelItineraryState.Canceled:
    case HotelItineraryState.CancellationFailure:
    case HotelItineraryState.Cancelled:
      return (
        <NotificationBanner
          className="status-banner"
          label={textConstants.CANCELED_RES}
          severity={BannerSeverity.ERROR}
          icon={<Icon name={IconName.ErrorAlert} />}
        />
      );
    default:
      return undefined;
  }
};

export interface IHotelCardProps
  extends HotelCardConnectorProps,
  RouteComponentProps<{}, StaticContext, { prevPath?: string }> {
  isMobile?: boolean;
  hotel: HotelItinerary;
  expandedCard: string;
  onExpandCard: (cardId: string) => void;
  isCorporate?: boolean;
  areTripsLoading?: boolean;
  showActivitiesXSell?: boolean;
}

export const HotelCard = ({
  history,
  hotel,
  isMobile,
  populateTripQueryParams,
  setSelectedHotel,
  onExpandCard,
  expandedCard,
  setOpenModal,
  isCorporate,
  tripsFilter,
  areTripsLoading,
  showActivitiesXSell = false,
}: IHotelCardProps) => {
  const isPastTrips = tripsFilter === MyTripsFilter.PAST_TRIPS;
  const hotelRef = useRef<HTMLDivElement | null>(null);
  const { isTravelApp } = useIsTravelApp();
  const hasTrackedView = useRef(false);

  const [inViewRef, inView] = useInView({
    threshold: 0.5, // Fire when 50% of card is visible
    triggerOnce: true, // Only fire once
    initialInView: false, // Don't fire on initial render
  });

  useEffect(() => {
    if (
      !isMobile &&
      hotelRef?.current &&
      expandedCard === hotel.reservation.reservationId
    ) {
      scrollTopWithOffset(hotelRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedCard]);

  // Fire tracking event when card becomes visible
  useEffect(() => {
    if (inView && !hasTrackedView.current && !areTripsLoading) {
      hasTrackedView.current = true;
      trackEvent({
        eventName: VIEWED_LODGING_RESERVATION_IN_LIST,
        properties: {
          reservation_booking_id: hotel.reservation.reservationBookingId,
          lodging_id: hotel.reservation.lodgingData?.id,
          lodging_name: hotel.reservation.lodgingData?.name,
          check_in_date: hotel.reservation.checkInDate,
          check_out_date: hotel.reservation.checkOutDate,
          hotel_confirmation_number_status:
            hotel.reservation.hotelConfirmationCode?.HotelConfirmationCode,
          active_tab: tripsFilter,
        } satisfies ViewedLodgingReservationInListProperties,
      });
    }
  }, [inView, hotel, tripsFilter, areTripsLoading]);

  const { ReservationState: reservationState } = hotel.reservation.state;
  const statusBanner = getStatusBanner(reservationState);
  const isCancel = [
    HotelItineraryState.Canceled,
    HotelItineraryState.CancellationFailure,
    HotelItineraryState.Cancelled,
  ].includes(reservationState);
  const confirmationCodeClassName = isCancel ? "warning" : "";

  const travelerNames = hotel.reservation.guests.map(
    (person) => `${person?.givenName} ${person?.surname}`,
  );

  const moreActions: IActionLink[] = showActivitiesXSell && hotel.reservation.lodgingData?.city
    ? buildExploreActivitiesActions({
      cityLabel: hotel.reservation.lodgingData.city,
      fallbackLabel: `${hotel.reservation.lodgingData.city}, ${hotel.reservation.lodgingData.state?.name?.trim() || hotel.reservation.lodgingData.state?.code?.trim()}, ${hotel.reservation.lodgingData.country}`,
      coords: hotel.reservation.lodgingData?.location?.coordinates,
      fromDate: hotel.reservation.checkInDate,
      untilDate: hotel.reservation.checkOutDate,
      isTravelApp,
      history,
      product: ItineraryEnum.Hotel,
    })
    : [];

  // Combine refs for both scrolling and intersection observer
  const setRefs = (node: HTMLDivElement | null) => {
    hotelRef.current = node;
    inViewRef(node);
  };

  return (
    <Box className={clsx({ mobile: isMobile }, "hotel-list")}>
      {isMobile ? (
        <div ref={setRefs}>
          <MyTripsMobileCard
            onClick={() => {
              setSelectedHotel(hotel);
              populateTripQueryParams(history);
            }}
            banner={statusBanner}
            confirmationCodeClassName={confirmationCodeClassName}
            confirmationLabel={textConstants.CONFIRMATION}
            label={
              hotel.reservation.lodgingCollection ===
                LodgingCollectionEnum.Premier
                ? textConstants.PREMIER_COLLECTION_HEADER_TEXT
                : hotel.reservation.lodgingCollection ===
                  LodgingCollectionEnum.Lifestyle
                  ? textConstants.LIFESYLE_COLLECTION_HEADER_TEXT
                  : textConstants.HOTEL_HEADER_TEXT
            }
            icon={IconName.HotelFunnelIcon}
            personalBusinessLabel={
              <PersonalBusinessLabel isCorporate={isCorporate} />
            }
            travelerNames={travelerNames}
            bottomContent={
              moreActions.length > 0 ? (
                <ActionLinks className="trip-info-bottom-content" actions={moreActions} />
              ) : undefined
            }
            {...textConstants.getHotelInfoDetails(hotel)}
          />
        </div>
      ) : (
        <div ref={setRefs}>
          <Box className="hotel-card-icon-title">
            <Icon name={IconName.HotelFunnelIcon} />
            <Typography variant="body1" className="summary-label">
              {textConstants.getHeaderText(
                hotel.reservation.checkInDate,
                hotel.reservation.checkOutDate,
                hotel.reservation.lodgingCollection ===
                LodgingCollectionEnum.Premier,
                hotel.reservation.lodgingCollection ===
                LodgingCollectionEnum.Lifestyle,
              )}
              <PersonalBusinessLabel isCorporate={isCorporate} />
            </Typography>
          </Box>
          <DesktopHotelCard
            banner={statusBanner}
            isPastTrips={isPastTrips}
            hotel={hotel}
            isCancel={isCancel}
            confirmationCodeClassName={confirmationCodeClassName}
            isMobile={isMobile}
            expandedCard={expandedCard}
            onExpandCard={onExpandCard}
            setOpenModal={setOpenModal}
            travelerNames={travelerNames}
            tripsFilter={tripsFilter}
            moreActions={moreActions}
          />
        </div>
      )}
    </Box>
  );
};
